import { referralGiftTask } from './referralTasks';
import { SUPPORT_ROLES } from './transactionService';
import { ref, get, push, set, update } from 'firebase/database';
import { database, auth } from '../firebaseConfig';
import { DEFAULT_TASK_LISTS } from '../data/taskListDefaults';
import { TEAM_TASK_LISTS } from '../data/teamTaskLists';
// Listing-expiration reminders are hidden from every list until 30 days before the listing expires.
import { visibleTasks, CLOSED_ONLY_TASKS } from '../utils/taskVisibility';
import { todayET } from '../utils/dueDates';

// The Internal NEW Contract email is always automatic (sent by the server when the deal goes under contract),
// even if a saved task list still has it as a manual quick-action email.
const isAlwaysAuto = (name) => /internal\s*-\s*new contract/i.test(name || '');

// Maps each date anchor used in the admin lists to the field that holds it on the buyer form.
const BUYER_ANCHOR_FIELDS = {
  underContractDate: 'contractDate',
  forecastedClosingDate: 'closingDate',
  earnestMoneyDueDate: 'firstEscrowDueDate',
  secondEmdDate: 'secondEscrowDueDate',
  dueDiligenceDeadline: 'homeInspectionDeadline',
  financingDeadline: 'financingDeadline',
  homeSaleContingencyDate: 'homeSaleContingencyDate',
};

// List used for each buyer type. Other types keep the existing generator until they are wired.
const BUYER_LIST_FOR_STATUS = {
  resale: 'resale-buyer-under-contract',
  'new-construction': 'new-construction-buyer-under-contract',
};

const addDays = (dateStr, days) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return null;
  d.setDate(d.getDate() + (Number(days) || 0));
  return d.toISOString().slice(0, 10);
};

// Loads the list as the admin last saved it; falls back to the starter list in code.
export const loadListTasks = async (listId) => {
  // Team lists always come from the code, so edits to data/teamTaskLists.js apply without a reset.
  if (TEAM_TASK_LISTS[listId]) {
    // Code tasks plus any tasks an admin added on the list since, so new tasks reach new and open deals.
    const codeTasks = TEAM_TASK_LISTS[listId].tasks;
    let saved = [];
    try {
      const snap = await get(ref(database, `taskLists/${listId}/tasks`));
      if (snap.exists()) saved = Object.values(snap.val());
    } catch (err) {
      console.error('Could not load added tasks for this list', err);
    }
    const codeNames = new Set(codeTasks.map(t => t.name));
    return [...codeTasks, ...saved.filter(t => t && t.name && !codeNames.has(t.name))];
  }
  try {
    const snap = await get(ref(database, `taskLists/${listId}/tasks`));
    if (snap.exists()) return Object.values(snap.val());
  } catch (err) {
    console.error('Could not load saved list, using starter list', err);
  }
  return (DEFAULT_TASK_LISTS[listId]?.tasks || TEAM_TASK_LISTS[listId]?.tasks || []);
};

// True when a task's required answer matches the transaction (e.g., association approval = Yes).
const passesRequirement = (task, formData) => {
  if (!task.requiresField) return true;
  return formData[task.requiresField] === task.requiresValue;
};

// Creates one transaction's tasks from the buyer list. Each task keeps its role, its visibility, and its status.
export const generateBuyerTasks = async (transactionId, formData, creatorUid) => {
  const listId = BUYER_LIST_FOR_STATUS[formData.propertyStatus];
  if (!listId) return { created: 0, skipped: 'list not wired for this buyer type yet' };

  const tasks = await loadListTasks(listId);
  let created = 0;
  let skipped = 0;

  for (const task of tasks) {
    if (!passesRequirement(task, formData)) { skipped += 1; continue; }

    const field = BUYER_ANCHOR_FIELDS[task.anchor];
    const anchorDate = field ? formData[field] : null;
    const dueDate = anchorDate ? addDays(anchorDate, task.offsetDays) : null;

    const record = {
      name: task.name,
      role: task.role,                 // Agent, TC, Marketing Manager
      type: task.type || 'Task',       // Task or Notification
      appliesTo: task.appliesTo || 'All', // 'All' shows to everyone on the transaction
      anchor: task.anchor || null,
      offsetDays: Number(task.offsetDays) || 0,
      shared: task.shared === true,
      action: /did this transaction close today/i.test(task.name || '') ? 'enter-closing-date' : null,
      mode: task.type === 'Notification' ? ((task.autoSend || isAlwaysAuto(task.name)) ? 'auto' : 'manual') : 'task',
      dueDate: dueDate,
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: creatorUid,
      createdAt: new Date().toISOString(),
    };

    if (task.type === 'Notification') {
      record.subject = task.subject || '';
      record.body = task.body || '';
      record.smsBody = task.smsBody || '';
      record.recipients = task.recipients || [];
    }

    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    created += 1;
  }

  return { created, skipped };
};

// Loads tasks from the given transactions, shaped like the personal tasks the dashboard already shows.
// Tasks that were replaced by a newer version. They stay hidden on existing deals.
const RETIRED_TASK_NAMES = [
  /^are there forms the buyer needs to complete for the hoa\?$/i,
  /^schedule final walk through$/i,
  /^text cooperating agent: confirm final walk-through$/i,
  /^First EMD Due Tomorrow: Confirm has been sent or is scheduled to be sent$/i,
];
const isRetired = (task) => RETIRED_TASK_NAMES.some(re => re.test(task?.name || ''));

export const loadTransactionTasks = async (transactionIds) => visibleTasks(await loadTransactionTasksRaw(transactionIds));
const loadTransactionTasksRaw = async (transactionIds) => {
  // One read per deal, all in parallel (was one after another).
  const perDeal = await Promise.all((transactionIds || []).map(async (txId) => {
    try {
      const snap = await get(ref(database, `transactions/${txId}/tasks`));
      if (!snap.exists()) return [];
      return Object.entries(snap.val())
        .filter(([, t]) => !isRetired(t))
        .map(([id, t]) => ({ ...t, id, title: t.name, transactionId: txId, source: 'transaction' }));
    } catch (err) {
      console.error('Could not load transaction tasks', err);
      return [];
    }
  }));
  return perDeal.flat();
};

// Marks a transaction task done and records who did it, so the other people on the deal see it.
export const completeTransactionTask = async (txId, taskId, completedByName) => {
  await update(ref(database, `transactions/${txId}/tasks/${taskId}`), {
    status: 'completed',
    completedBy: completedByName || 'Unknown',
    completedByUid: auth?.currentUser?.uid || null,
    completedAt: new Date().toISOString(),
  });
};


// Tasks this user should see across RESIDENCE deals.
// Support roles see every RESIDENCE deal for their role. Everyone else sees only their own deals.
// Tasks marked for everyone on the deal show on any deal the user can see.
export const loadTeamTransactionTasks = async (uid, userRole) => visibleTasks(await loadTeamTransactionTasksRaw(uid, userRole));
const loadTeamTransactionTasksRaw = async (uid, userRole) => {
  const indexSnap = await get(ref(database, 'transactionIndex'));
  if (!indexSnap.exists()) return [];
  const isSupport = SUPPORT_ROLES.includes(userRole);
  const entries = Object.entries(indexSnap.val())
    .map(([id, e]) => ({ id, ...e }))
    .filter(e => isSupport || e.ownerUid === uid || Boolean(e.coAgentUids?.[uid]));
  const ownerById = Object.fromEntries(entries.map(e => [e.id, e.ownerUid]));
  const rows = await loadTransactionTasks(entries.map(e => e.id));
  const roles = userRole === 'Admin' ? ['Admin', 'Agent'] : userRole === 'limited_agent' ? ['Agent'] : [userRole];
  const coById = Object.fromEntries(entries.map(e => [e.id, Boolean(e.coAgentUids?.[uid])]));
  // viewerIsDealAgent is display-only (never saved): lets My tasks treat owners and co-agents alike.
  return rows.map(t => ({ ...t, viewerIsDealAgent: ownerById[t.transactionId] === uid || coById[t.transactionId] })).filter(t => {
    const isOwner = ownerById[t.transactionId] === uid;
    if (isOwner || isSupport) return t.shared === true || roles.includes(t.role || 'Agent');
    // A co-agent on the deal works the agent's tasks, so both agents can collaborate on one list.
    return t.shared === true || (t.role || 'Agent') === 'Agent';
  });
};

// Seller, landlord and tenant lists, and which form date each anchor reads from.
const TEAM_LIST_FOR_TYPE = { Seller: 'seller-listing', Landlord: 'landlord-rental-listing', Tenant: 'tenant-rental' };
const TEAM_ANCHOR_FIELDS = {
  Seller: {
    targetListDate: 'anticipatedListingDate',
    mlsLiveDate: 'anticipatedListingDate',
    photographerDate: 'photographyDate',
    listingExpirationDate: 'listingExpirationDate',
  },
  // Landlord and tenant anchors read from the lease dates on the form. Confirm these mappings.
  Landlord: {
    targetListDate: 'leaseStartDate',
    mlsLiveDate: 'leaseStartDate',
    photographerDate: 'leaseStartDate',
    listingExpirationDate: 'leaseEndDate',
  },
  Tenant: {
    underContractDate: 'leaseStartDate',
    moveInDate: 'moveInDate',
    moveOutDate: 'leaseEndDate',
    forecastedClosingDate: 'moveInDate',
  },
};

// Creates one seller, landlord or tenant deal's tasks from its list. "created" means today.
// Kelly Olin (team leader). On her own listings, Stacey (Marketing Manager) adds the listing to Linktree;
// on every other agent's listing it stays with that agent. Applied when tasks are generated (new deals only).
export const TEAM_LEADER_UID = 'izVJWBDFcgZRFM1lzFDd8K5P6bU2';
export const roleForGeneratedTask = (task, listingAgentUid) =>
  (/^add listing to linktree$/i.test(String(task?.name || '').trim()) && listingAgentUid === TEAM_LEADER_UID ? 'Marketing Manager' : task.role);

export const generateTeamTasks = async (transactionId, formData, creatorUid, transactionType) => {
  const listId = TEAM_LIST_FOR_TYPE[transactionType];
  if (!listId) return { created: 0, skipped: 'no list for this type' };

  const tasks = await loadListTasks(listId);
  const fields = TEAM_ANCHOR_FIELDS[transactionType] || {};
  const today = todayET();
  let created = 0;

  for (const task of tasks) {
    if (!passesRequirement(task, formData)) continue;
    const anchorDate = task.anchor === 'created' ? today : (fields[task.anchor] ? formData[fields[task.anchor]] : null);
    const dueDate = anchorDate ? addDays(anchorDate, task.offsetDays) : null;

    const record = {
      name: task.name,
      role: roleForGeneratedTask(task, formData?.ownerUid || creatorUid),
      type: task.type || 'Task',
      appliesTo: task.appliesTo || 'All',
      mode: task.type === 'Notification' ? ((task.autoSend || isAlwaysAuto(task.name)) ? 'auto' : 'manual') : 'task',
      dueDate: dueDate,
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: creatorUid,
      createdAt: new Date().toISOString(),
    };
    if (task.description) record.description = task.description;
    if (task.liveCheck) record.liveCheck = true;

    if (task.type === 'Notification') {
      record.subject = task.subject || '';
      record.body = task.body || '';
      record.smsBody = task.smsBody || '';
      record.recipients = task.recipients || [];
    }

    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    created += 1;
  }
  return { created };
};

// Starts the Live Listing tasks (ShowingTime setup and the MLS live follow-ups) once the listing is live.
// Tasks already on the deal are skipped. Due dates count from the deal's anchor date, or from today if none is set.
// A backdated live date must never produce past-due work or late emails. Tasks whose computed due date
// is already past are due today instead; automatic emails that would have gone out in the past are
// skipped (marked, never sent). Pure: used by activateLiveListingTasks and the MCP server, and tested.
export const backdateSafeTask = (record, today) => {
  if (!record || !record.dueDate || !today || record.dueDate >= today) return record;
  if (record.type === 'Notification') {
    return { ...record, mode: 'manual', status: 'skipped', skippedReason: 'Live date was backdated: this email date had already passed, so it was not sent.', originalDueDate: record.dueDate };
  }
  return { ...record, dueDate: today, originalDueDate: record.dueDate };
};

export const activateLiveListingTasks = async (transactionId, { liveDate } = {}) => {
  if (!transactionId) return { created: 0 };
  const tasks = (await loadListTasks('seller-listing')).filter(t => t.requiresField === 'listingStatus' && t.requiresValue === 'live' && !t.liveCheck);
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const existing = new Set(snap.exists() ? Object.values(snap.val()).map(t => t.name) : []);
  const txSnap = await get(ref(database, `transactions/${transactionId}`));
  const tx = txSnap.exists() ? txSnap.val() : {};
  const _n = new Date(); const today = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`;
  let created = 0;
  for (const task of tasks) {
    if (existing.has(task.name)) continue;
    // Count from the deal's own date for this anchor (for example, the Anticipated Listing Date for MLS live), not from today.
    const anchorField = task.anchor ? TEAM_ANCHOR_FIELDS.Seller[task.anchor] : null;
    const anchorDate = (task.anchor === 'mlsLiveDate' && liveDate) ? liveDate : (anchorField ? tx[anchorField] : null);
    let record = {
      name: task.name,
      role: task.role,
      type: task.type || 'Task',
      appliesTo: task.appliesTo || 'All',
      mode: task.type === 'Notification' ? ((task.autoSend || isAlwaysAuto(task.name)) ? 'auto' : 'manual') : 'task',
      dueDate: addDays(anchorDate || today, task.offsetDays || 0),
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: auth?.currentUser?.uid || null,
      createdAt: new Date().toISOString(),
    };
    if (task.description) record.description = task.description;
    if (task.type === 'Notification') {
      record.subject = task.subject || '';
      record.body = task.body || '';
      record.smsBody = task.smsBody || '';
      record.recipients = task.recipients || [];
    }
    record = backdateSafeTask(record, today);
    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    created += 1;
  }
  return { created };
};

// One place to record the "Date listed live on MLS". Sets the deal Live and starts the live-listing tasks
// (backdate-safe). A deal that is already under contract, closed or lost only gets the date recorded:
// its status never moves back to Live and no live-listing tasks are added.
export const setListingLiveDate = async (transactionId, ownerUid, liveDate, byName = 'Unknown') => {
  if (!transactionId || !ownerUid || !/^\d{4}-\d{2}-\d{2}$/.test(liveDate || '')) throw new Error('A live date (YYYY-MM-DD) is required.');
  const txSnap = await get(ref(database, `users/${ownerUid}/transactions/${transactionId}`));
  const tx = txSnap.exists() ? txSnap.val() : {};
  const liveAt = new Date(`${liveDate}T12:00:00`).toISOString();
  const pastListing = ['pending', 'under-contract', 'closed', 'lost'].includes(tx.status) || tx.listingStatus === 'pending';
  if (pastListing) {
    await update(ref(database, `users/${ownerUid}/transactions/${transactionId}`), { liveDate, liveAt, updatedAt: new Date().toISOString() });
    await closeMlsCheckTasks(transactionId, byName);
    return { statusChanged: false, created: 0 };
  }
  await update(ref(database, `users/${ownerUid}/transactions/${transactionId}`), { liveDate, liveAt, status: 'live', listingStatus: 'live', updatedAt: new Date().toISOString() });
  await update(ref(database, `transactions/${transactionId}`), { listingStatus: 'live' }).catch(() => {});
  const { created } = await activateLiveListingTasks(transactionId, { liveDate });
  await closeMlsCheckTasks(transactionId, byName);
  return { statusChanged: true, created };
};

// Completes open document-collection tasks (e.g. "Upload BBA") once that document is uploaded on the deal.
export const completeDocTasks = async (transactionId, categories, completedByName) => {
  const { tasksToComplete } = await import('../shared/transactionDocs');
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  if (!snap.exists()) return 0;
  const ids = tasksToComplete(snap.val(), categories);
  for (const id of ids) await completeTransactionTask(transactionId, id, completedByName);
  return ids.length;
};

// Closes the open "Has this listing gone live on the MLS?" (and Mark Listing as Live) tasks once the listing is live.
export const closeMlsCheckTasks = async (transactionId, completedByName) => {
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  if (!snap.exists()) return 0;
  let closed = 0;
  for (const [taskId, t] of Object.entries(snap.val())) {
    if (!t || t.status === 'completed') continue;
    if (!/gone live on the mls\?|^mark listing as live$/i.test(t.name || '')) continue;
    await completeTransactionTask(transactionId, taskId, completedByName);
    closed += 1;
  }
  return closed;
};

// Records the Matterport appointment: completes the scheduling task and sets a reminder task for the day before the scan.
// If a reminder already exists (for example, after a reschedule), its date and time are updated instead.
export const scheduleMatterportScan = async (transactionId, appointmentDate, appointmentTime, completedByName) => {
  if (!transactionId || !appointmentDate) return;
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const all = snap.exists() ? Object.entries(snap.val()) : [];
  for (const [taskId, t] of all) {
    if (!t || t.status === 'completed') continue;
    if (/^schedule matterport scan$/i.test(t.name || '')) await completeTransactionTask(transactionId, taskId, completedByName);
  }
  const when = `${appointmentDate}${appointmentTime ? ` at ${appointmentTime}` : ''}`;
  const description = `Matterport scan on ${when}. Text the client a reminder the day before.`;
  const reminderName = 'Text Client: Matterport Scan Reminder';
  const existing = all.find(([, t]) => t && t.name === reminderName && t.status !== 'completed');
  if (existing) {
    await update(ref(database, `transactions/${transactionId}/tasks/${existing[0]}`), {
      dueDate: addDays(appointmentDate, -1),
      description,
    });
    return;
  }
  await set(push(ref(database, `transactions/${transactionId}/tasks`)), {
    name: reminderName,
    role: 'Local Assistant',
    type: 'Task',
    appliesTo: 'Seller',
    mode: 'task',
    dueDate: addDays(appointmentDate, -1),
    status: 'pending',
    completedBy: null,
    completedAt: null,
    description,
    createdBy: auth?.currentUser?.uid || null,
    createdAt: new Date().toISOString(),
  });
};

// The Under Contract form saves its dates under underContract with its own field names.
const UNDER_CONTRACT_FIELDS = {
  contractDate: 'underContractDate',
  closingDate: 'forecastedClosedDate',
  firstEscrowDueDate: 'earnestMoneyDueDate',
  secondEscrowDueDate: 'secondEmdDue',
  homeInspectionDeadline: 'dueDiligenceDeadline',
  financingDeadline: 'financingContingencyDeadline',
  homeSaleContingencyDate: 'homeSaleContingencyDate',
};

// Current date for an anchor: the Under Contract form's answer first, then the buyer form's.
const dateFromTransaction = (tx, field) =>
  tx?.underContract?.[UNDER_CONTRACT_FIELDS[field]] || tx?.[field] || null;

// Recomputes open tasks' due dates from the deal's current dates. Completed and N/A tasks are left alone.
export const syncTaskDates = async (transactionId, tx) => {
  // Under contract with a referred client: one "Send [referrer] a thank-you gift" task (never sent anywhere).
  try { await referralGiftTask(transactionId, tx); } catch (e) { console.error('Referral gift task skipped', e); }
  const listId = BUYER_LIST_FOR_STATUS[tx?.propertyStatus];
  if (!listId || !transactionId) return { updated: 0 };
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  if (!snap.exists()) return { updated: 0 };
  const listTasks = await loadListTasks(listId);
  const byName = Object.fromEntries(listTasks.map(t => [t.name, t]));
  const updates = {};
  Object.entries(snap.val()).forEach(([taskId, task]) => {
    if (task.status === 'completed' || task.status === 'na' || task.status === 'skipped' || task.originalDueDate) return;
    const def = byName[task.name];
    const anchor = task.anchor || def?.anchor;
    if (!anchor) return;
    const offset = task.offsetDays ?? def?.offsetDays ?? 0;
    const field = BUYER_ANCHOR_FIELDS[anchor];
    const anchorDate = field ? dateFromTransaction(tx, field) : null;
    const dueDate = anchorDate ? addDays(anchorDate, offset) : null;
    if ((task.dueDate || null) !== dueDate) {
      updates[`transactions/${transactionId}/tasks/${taskId}/dueDate`] = dueDate;
    }
  });
  if (Object.keys(updates).length) await update(ref(database), updates);
  return { updated: Object.keys(updates).length };
};

// Starts the seller under-contract list when a listing goes under contract. Skips any task the deal already has,
// so calling it twice does not duplicate tasks. dates: { underContractDate, closingDate } as YYYY-MM-DD.
const SELLER_UNDER_CONTRACT_LIST = 'seller-under-contract-team';
// One "Home Anniversary" task per year on the closing date, for ten years. The agent reaches out and wishes them well.
const generateHomeAnniversaryTasks = async (transactionId, closedDate, creatorUid, years = 10) => {
  if (!closedDate) return 0;
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const existing = new Set(snap.exists() ? Object.values(snap.val()).map(t => `${t.name}|${t.dueDate}`) : []);
  const baseYear = Number(closedDate.slice(0, 4));
  const mmdd = closedDate.slice(5);
  let created = 0;
  for (let n = 1; n <= years; n += 1) {
    const dueDate = `${baseYear + n}-${mmdd}`;
    const name = `Home Anniversary Year ${n}: Reach Out and Wish Them Well`;
    if (existing.has(`${name}|${dueDate}`)) continue;
    const record = {
      name,
      role: 'Agent',
      type: 'Task',
      appliesTo: 'All',
      mode: 'task',
      dueDate,
      anchor: 'actualClosingDate',
      offsetDays: 365 * n,
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: creatorUid,
      createdAt: new Date().toISOString(),
      description: 'Reach out and wish them well on their home anniversary.',
    };
    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    created += 1;
  }
  return created;
};

// Starts the Post Closing Follow Up list once a deal is marked closed. Every date in the list is counted from
// the actual closing date, so the offsets in the list are days after closing.
// Creates a yearly "Wish ... Happy Birthday" task for the agent for each client birthday, for the next ten years.
// people: [{ name, date: "YYYY-MM-DD" }]. Skips any year that already has the same task.
export const generateBirthdayTasks = async (transactionId, people, creatorUid, years = 10) => {
  if (!transactionId || !people?.length) return { created: 0 };
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const existing = new Set(snap.exists() ? Object.values(snap.val()).map(t => `${t.name}|${t.dueDate}`) : []);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  let created = 0;
  for (const person of people) {
    if (!person?.name || !person?.date) continue;
    const mmdd = person.date.slice(5);
    const startYear = now.getFullYear();
    for (let i = 0; i < years; i += 1) {
      const dueDate = `${startYear + i}-${mmdd}`;
      if (dueDate < today) continue;
      const name = `Wish ${person.name} Happy Birthday`;
      if (existing.has(`${name}|${dueDate}`)) continue;
      const record = {
        name,
        role: 'Agent',
        type: 'Task',
        appliesTo: 'All',
        mode: 'task',
        dueDate,
        anchor: 'clientBirthday',
        offsetDays: 0,
        status: 'pending',
        completedBy: null,
        completedAt: null,
        createdBy: creatorUid,
        createdAt: new Date().toISOString(),
        description: `It's ${person.name}'s birthday. Send a happy birthday wish.`,
      };
      await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
      existing.add(`${name}|${dueDate}`);
      created += 1;
    }
  }
  return { created };
};

// Called by every close path. Shows the post-closing tasks (Update MLS Status to Closed, Just Sold Social Media Post)
// due on the closed date: existing hidden ones are released and re-dated, missing ones are created. Safe to call twice.
const CLOSED_ONLY_DEFS = [
  { match: /update mls status to closed/i, sellerOnly: true, name: 'Update MLS Status to Closed (Sold)', role: 'TC', description: 'The deal is closed: change the listing status on the MLS to Closed (Sold).' },
  { match: /just sold social media post/i, name: 'Just Sold Social Media Post!', role: 'Marketing Manager' },
  // Only when "Create Just Sold Postcards" was ticked on the seller form. Never exists before the deal is Closed.
  { match: /^create just sold postcards$/i, sellerOnly: true, onlyIf: 'justSoldPostcards', name: 'Create Just Sold Postcards', role: 'Marketing Manager', description: 'The listing has closed. Design the just sold postcards, paste the link and send them to the agent for approval. Order them once the agent approves.' },
];
export const releaseClosedTasks = async (transactionId, closedDate, creatorUid, txType = 'Seller', opts = {}) => {
  if (!transactionId || !closedDate) return { released: 0, created: 0 };
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const all = snap.exists() ? Object.entries(snap.val()) : [];
  const updates = {}; let released = 0; let created = 0;
  for (const def of CLOSED_ONLY_DEFS) {
    if (def.sellerOnly && String(txType || '').toLowerCase() !== 'seller') continue;
    if (def.onlyIf && !opts[def.onlyIf]) continue;
    const mine = all.filter(([, t]) => t && def.match.test(t.name || ''));
    if (mine.length) {
      mine.forEach(([id, t]) => {
        if (t.closedRelease || t.status === 'completed' || t.status === 'na') return;
        updates[`transactions/${transactionId}/tasks/${id}/closedRelease`] = true;
        updates[`transactions/${transactionId}/tasks/${id}/dueDate`] = closedDate;
        released += 1;
      });
    } else {
      await set(push(ref(database, `transactions/${transactionId}/tasks`)), {
        name: def.name, role: def.role, type: 'Task', appliesTo: 'All', mode: 'task', dueDate: closedDate, status: 'pending',
        closedRelease: true, completedBy: null, completedAt: null, createdBy: creatorUid || null, createdAt: new Date().toISOString(),
        ...(def.description ? { description: def.description } : {}),
      });
      created += 1;
    }
  }
  if (Object.keys(updates).length) await update(ref(database), updates);
  return { released, created };
};

export const startPostClosingFollowUp = async (transactionId, closedDate, creatorUid) => {
  if (!transactionId || !closedDate) return { created: 0 };
  const tasks = await loadListTasks('post-closing-follow-up');
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const existing = new Set(snap.exists() ? Object.values(snap.val()).map(t => t.name) : []);
  let created = 0;
  for (const task of tasks) {
    if (existing.has(task.name)) continue;
    if (task.name === 'Annual Homiversary') continue;
    const record = {
      name: task.name,
      role: task.role,
      type: task.type || 'Task',
      appliesTo: task.appliesTo || 'All',
      mode: task.type === 'Notification' ? ((task.autoSend || isAlwaysAuto(task.name)) ? 'auto' : 'manual') : 'task',
      dueDate: addDays(closedDate, task.offsetDays || 0),
      anchor: 'actualClosingDate',
      offsetDays: task.offsetDays || 0,
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: creatorUid,
      createdAt: new Date().toISOString(),
    };
    if (task.description) record.description = task.description;
    if (task.type === 'Notification') {
      record.subject = task.subject || '';
      record.body = task.body || '';
      record.smsBody = task.smsBody || '';
      record.recipients = task.recipients || [];
    }
    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    created += 1;
  }
  created += await generateHomeAnniversaryTasks(transactionId, closedDate, creatorUid);
  return { created };
};

export const startSellerUnderContract = async (transactionId, dates, creatorUid) => {
  if (!transactionId) return { created: 0, skipped: 'no deal' };
  await update(ref(database, `transactions/${transactionId}`), { listingStatus: 'pending' }).catch(() => {});
  const tasks = await loadListTasks(SELLER_UNDER_CONTRACT_LIST);
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const existing = new Set(snap.exists() ? Object.values(snap.val()).map(t => t.name) : []);
  const today = new Date().toISOString().slice(0, 10);
  let created = 0;

  for (const task of tasks) {
    if (existing.has(task.name)) continue;
    if (CLOSED_ONLY_TASKS.test(task.name || '')) continue; // created when the deal is marked Closed (releaseClosedTasks)
    if (task.requiresField && dates?.[task.requiresField] !== task.requiresValue) continue;
    const anchorDate = task.anchor === 'created' ? today : (dates?.[task.anchor] || null);
    const dueDate = anchorDate ? addDays(anchorDate, task.offsetDays) : null;

    const record = {
      name: task.name,
      role: task.role,
      type: task.type || 'Task',
      appliesTo: task.appliesTo || 'All',
      mode: task.type === 'Notification' ? ((task.autoSend || isAlwaysAuto(task.name)) ? 'auto' : 'manual') : 'task',
      dueDate: dueDate,
      anchor: task.anchor,
      offsetDays: task.offsetDays,
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: creatorUid,
      createdAt: new Date().toISOString(),
    };
    if (task.description) record.description = task.description;
    if (task.type === 'Notification') {
      record.subject = task.subject || '';
      record.body = task.body || '';
      record.smsBody = task.smsBody || '';
      record.recipients = task.recipients || [];
    }
    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    created += 1;
  }
  return { created };
};

// Agent reminders before a seller listing expires. They are not sent to the client.
const EXPIRATION_REMINDERS = [
  // One combined task per interval (agent reminder + manual client text). aliases are the old task names, so
  // reminders already on live deals are still recognized and no duplicate is added; they are not renamed.
  { name: 'Listing Expires in 30 Days: Text Client & Remind Agent', offset: -30, aliases: ['Listing Expiring in 30 Days: Remind Agent'] },
  { name: 'Listing Expires in 15 Days: Text Client & Remind Agent', offset: -15, aliases: ['Listing Expiring in 15 Days: Remind Agent'] },
  { name: 'Listing Expires in 7 Days: Text Client & Remind Agent', offset: -7, aliases: ['Listing Expiring in 7 Days: Remind Agent'] },
  { name: 'Listing Expires Tomorrow: Text Client & Remind Agent', offset: -1, aliases: ['Listing Expires Tomorrow: Remind Agent'] },
];
export const EXPIRATION_REMINDER_NAMES = EXPIRATION_REMINDERS.flatMap(r => [r.name, ...(r.aliases || [])]);
export const isListingExpirationTask = (task) => task?.anchor === 'listingExpirationDate' || /listing\s+expir/i.test(String(task?.name || ''));
const isUnderContractDeal = (tx) => ['pending', 'under-contract', 'closed', 'lost'].includes(tx?.status) || !!tx?.underContract;

// Keeps the reminders in step with the deal. Reminders are removed while the listing is under contract or has no
// expiration date. A reminder whose date has passed is removed. A reminder that applies again after the expiration
// date is extended is added back. Completed reminders are never changed.
export const syncExpirationReminders = async (transactionId, tx, creatorUid) => {
  if (!transactionId || !tx) return { created: 0, removed: 0 };
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  const existing = snap.exists() ? snap.val() : {};
  const exp = tx.listingExpirationDate || null;
  const today = new Date().toISOString().slice(0, 10);
  const active = !!exp && !isUnderContractDeal(tx);
  const updates = {};
  const haveNames = new Set();
  let removed = 0;

  Object.entries(existing).forEach(([taskId, task]) => {
    const reminder = EXPIRATION_REMINDERS.find(r => r.name === task.name || (r.aliases || []).includes(task.name));
    if (!reminder) {
      // Any other open listing-expiration task (custom list names such as "Text Client: Listing Expires in 30 Days",
      // or anything anchored on the expiration date) is not needed once the deal is under contract.
      const open = !['completed', 'na', 'skipped'].includes(task.status);
      if (open && isUnderContractDeal(tx) && isListingExpirationTask(task)) {
        updates[`transactions/${transactionId}/tasks/${taskId}`] = null;
        removed += 1;
      }
      return;
    }
    const finished = task.status === 'completed' || task.status === 'na';
    if (finished) {
      // A finished reminder is kept as history. It only counts for the expiration date it was made for,
      // so an extension gets its own reminder. Older tasks without that date count as the current one.
      if ((task.forExpiration || exp) === exp) haveNames.add(reminder.name);
      return;
    }
    const due = active ? addDays(exp, reminder.offset) : null;
    if (!active || due < today) {
      updates[`transactions/${transactionId}/tasks/${taskId}`] = null;
      removed += 1;
      return;
    }
    haveNames.add(reminder.name);
    if ((task.dueDate || null) !== due) updates[`transactions/${transactionId}/tasks/${taskId}/dueDate`] = due;
    if (task.forExpiration !== exp) updates[`transactions/${transactionId}/tasks/${taskId}/forExpiration`] = exp;
  });

  let created = 0;
  if (active) {
    for (const r of EXPIRATION_REMINDERS) {
      if (haveNames.has(r.name)) continue;
      const due = addDays(exp, r.offset);
      if (due < today) continue;
      const newKey = push(ref(database, `transactions/${transactionId}/tasks`)).key;
      updates[`transactions/${transactionId}/tasks/${newKey}`] = {
        name: r.name,
        role: 'Agent',
        type: 'Task',
        appliesTo: 'Seller',
        mode: 'task',
        dueDate: due,
        anchor: 'listingExpirationDate',
        offsetDays: r.offset,
        forExpiration: exp,
        status: 'pending',
        completedBy: null,
        completedAt: null,
        createdBy: creatorUid || null,
        createdAt: new Date().toISOString(),
      };
      created += 1;
    }
  }

  if (Object.keys(updates).length) await update(ref(database), updates);
  return { created, removed };
};

// Seller, landlord and tenant deals: recalculates open tasks' due dates when the deal's dates change.
// Uses the team list for the deal's type, and the same date fields the team form uses.
const TEAM_LISTS_FOR_TYPE_SYNC = {
  Seller: ['seller-listing', 'seller-under-contract-team'],
  Landlord: ['landlord-rental-listing'],
  Tenant: ['tenant-rental'],
};
export const teamDateFor = (tx, anchor) => {
  const fields = TEAM_ANCHOR_FIELDS[tx?.type] || {};
  if (fields[anchor]) return tx[fields[anchor]] || null;
  if (anchor === 'underContractDate') return tx.underContract?.underContractDate || tx.underContract?.contractDate || tx.underContractDate || null;
  // The Under Contract form's "Anticipated Closing Date" is underContract.forecastedClosedDate.
  if (anchor === 'closingDate') return tx.underContract?.forecastedClosedDate || tx.closingDate || tx.underContract?.closingDate || tx.forecastedClosedDate || null;
  if (anchor === 'settlementDate') return tx.actualClosingDate || null;
  if (anchor === 'dueDiligenceDeadline') return tx.underContract?.dueDiligenceDeadline || null;
  if (anchor === 'homeSaleContingencyDate') return tx.underContract?.homeSaleContingencyDate || null;
  return null;
};
export const syncTeamTaskDates = async (transactionId, tx) => {
  const listIds = TEAM_LISTS_FOR_TYPE_SYNC[tx?.type];
  if (!listIds || !transactionId) return { updated: 0 };
  const snap = await get(ref(database, `transactions/${transactionId}/tasks`));
  if (!snap.exists()) return { updated: 0 };
  const defs = {};
  for (const listId of listIds) {
    (await loadListTasks(listId)).forEach(d => { defs[d.name] = d; });
  }
  const updates = {};
  Object.entries(snap.val()).forEach(([taskId, task]) => {
    if (task.status === 'completed' || task.status === 'na' || task.status === 'skipped' || task.originalDueDate) return;
    const def = defs[task.name];
    const anchor = task.anchor || def?.anchor;
    if (!anchor || anchor === 'created') return;
    const offset = task.offsetDays ?? def?.offsetDays ?? 0;
    const anchorDate = teamDateFor(tx, anchor);
    const dueDate = anchorDate ? addDays(anchorDate, offset) : null;
    if ((task.dueDate || null) !== dueDate) {
      updates[`transactions/${transactionId}/tasks/${taskId}/dueDate`] = dueDate;
    }
  });
  // Adds seller listing tasks the deal now qualifies for (for example, partial staging), skipping any it already has.
  const existingNames = new Set(Object.values(snap.val()).map(t => t.name));
  const todayStr = new Date().toISOString().slice(0, 10);
  for (const def of await loadListTasks('seller-listing')) {
    if (!def.requiresField || def.liveCheck || existingNames.has(def.name) || !passesRequirement(def, tx)) continue;
    const anchorDate = def.anchor === 'created' ? todayStr : teamDateFor(tx, def.anchor);
    const record = {
      name: def.name,
      role: def.role,
      type: def.type || 'Task',
      appliesTo: def.appliesTo || 'All',
      mode: def.type === 'Notification' ? (def.autoSend ? 'auto' : 'manual') : 'task',
      dueDate: anchorDate ? addDays(anchorDate, def.offsetDays || 0) : null,
      status: 'pending',
      completedBy: null,
      completedAt: null,
      createdBy: null,
      createdAt: new Date().toISOString(),
    };
    if (def.description) record.description = def.description;
    await set(push(ref(database, `transactions/${transactionId}/tasks`)), record);
    existingNames.add(def.name);
  }
  if (Object.keys(updates).length) await update(ref(database), updates);
  if (tx?.type === 'Seller') await syncExpirationReminders(transactionId, tx, null);
  return { updated: Object.keys(updates).length };
};

// One-time cleanup: permanently removes retired tasks from every RESIDENCE deal this user can reach.
export const purgeRetiredTasks = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  let removed = 0;
  for (const txId of Object.keys(idx.val() || {})) {
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    const updates = {};
    Object.entries(snap.val() || {}).forEach(([taskId, t]) => {
      if (isRetired(t)) updates[`transactions/${txId}/tasks/${taskId}`] = null;
    });
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      removed += n;
    }
  }
  return removed;
};

// One-time update for existing resale buyer deals: the EMD auto emails lose their text,
// and the manual Text Client tasks are added (or marked shared) so the TC and agent can both send them.
export const migrateEmdTexts = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  const listTasks = await loadListTasks(BUYER_LIST_FOR_STATUS.resale);
  const textDefs = listTasks.filter(t => /^Text Client: .*EMD/i.test(t.name || ''));
  const AUTO_EMD_NAMES = [
    'Auto Email to Client: First EMD Due Tomorrow',
    'Auto Email to Client: Second EMD Due Tomorrow',
  ];
  let changed = 0;
  for (const [txId, entry] of Object.entries(idx.val() || {})) {
    const txSnap = await get(ref(database, `users/${entry.ownerUid}/transactions/${txId}`));
    if (!txSnap.exists()) continue;
    const tx = txSnap.val();
    if (tx.propertyStatus !== 'resale') continue;
    const taskSnap = await get(ref(database, `transactions/${txId}/tasks`));
    const existing = taskSnap.exists() ? taskSnap.val() : {};
    const updates = {};
    const byName = {};
    Object.entries(existing).forEach(([taskId, t]) => {
      byName[t.name] = { taskId, ...t };
      if (AUTO_EMD_NAMES.includes(t.name) && t.smsBody) {
        updates[`transactions/${txId}/tasks/${taskId}/smsBody`] = null;
      }
    });
    for (const def of textDefs) {
      const have = byName[def.name];
      if (have) {
        if (have.shared !== true) updates[`transactions/${txId}/tasks/${have.taskId}/shared`] = true;
        continue;
      }
      if (def.requiresAnchor && !dateFromTransaction(tx, BUYER_ANCHOR_FIELDS[def.requiresAnchor])) continue;
      const field = BUYER_ANCHOR_FIELDS[def.anchor];
      const anchorDate = field ? dateFromTransaction(tx, field) : null;
      const newKey = push(ref(database, `transactions/${txId}/tasks`)).key;
      updates[`transactions/${txId}/tasks/${newKey}`] = {
        name: def.name,
        role: def.role,
        type: def.type || 'Task',
        appliesTo: def.appliesTo || 'All',
        mode: 'task',
        dueDate: anchorDate ? addDays(anchorDate, def.offsetDays) : null,
        status: 'pending',
        completedBy: null,
        completedAt: null,
        createdBy: entry.ownerUid,
        createdAt: new Date().toISOString(),
        anchor: def.anchor || null,
        offsetDays: Number(def.offsetDays) || 0,
        shared: true,
        action: null,
      };
    }
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      changed += n;
    }
  }
  return changed;
};

// One-time update: tasks whose role changed in the lists are switched on existing deals too.
const ROLE_CHANGES = {
  'Create Bold Trail Follow Up Campaign': 'Marketing Manager',
  'Create Meta Ad Campaign': 'Marketing Manager',
  'Brochures - Design, Post Link for Review, Order': 'Marketing Manager',
  'Brochures: Design, Post Link for Review, Order': 'Marketing Manager',
  'AUTO EMAIL to Schedule Matterport Scan': 'Local Assistant',
  'Connect with Client about Setup/Removal of Utilities': 'TC',
  'Has Buyer Gotten HOA/Condo Approval?': 'TC',
  'Second EMD Due: Confirm second earnest money has been sent or is scheduled to be sent': 'TC',
};
export const migrateTaskRoles = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  let changed = 0;
  for (const txId of Object.keys(idx.val() || {})) {
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    const updates = {};
    Object.entries(snap.val() || {}).forEach(([taskId, t]) => {
      const role = ROLE_CHANGES[t.name];
      if (role && t.role !== role) updates[`transactions/${txId}/tasks/${taskId}/role`] = role;
    });
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      changed += n;
    }
  }
  return changed;
};

// One-time cleanup: the duplicate utilities task is removed from buyer and seller deals.
// Landlord and tenant deals keep theirs, since that is their only utilities task.
const DUPLICATE_UTILITIES_TASK = 'Make Sure Clients Utilities are Set Up/Removed';
export const removeDuplicateUtilitiesTask = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  let removed = 0;
  for (const [txId, entry] of Object.entries(idx.val() || {})) {
    const txSnap = await get(ref(database, `users/${entry.ownerUid}/transactions/${txId}`));
    const type = txSnap.exists() ? txSnap.val().type : '';
    if (type === 'Landlord' || type === 'Tenant') continue;
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    const updates = {};
    Object.entries(snap.val() || {}).forEach(([taskId, t]) => {
      if (t.name === DUPLICATE_UTILITIES_TASK) updates[`transactions/${txId}/tasks/${taskId}`] = null;
    });
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      removed += n;
    }
  }
  return removed;
};

// One-time cleanup: buyer deals marked "No" for home to sell contingency lose the home sale check task.
export const removeHomeSaleTasksWhereNone = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  let removed = 0;
  for (const [txId, entry] of Object.entries(idx.val() || {})) {
    const txSnap = await get(ref(database, `users/${entry.ownerUid}/transactions/${txId}`));
    if (!txSnap.exists()) continue;
    const tx = txSnap.val();
    // The answer may be saved on the buyer form or on the Under Contract form.
    const answers = [tx.homeSaleContingency, tx.underContract?.homeSaleContingency];
    if (answers.includes('Yes') || !answers.includes('No')) continue;
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    const updates = {};
    Object.entries(snap.val() || {}).forEach(([taskId, t]) => {
      if (/^Transaction has Home Sale Contingency/i.test(t.name || '')) updates[`transactions/${txId}/tasks/${taskId}`] = null;
    });
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      removed += n;
    }
  }
  return removed;
};

// Brings one deal's task list up to date each time it opens: removes the home sale check when the deal says No,
// removes the duplicate utilities task (not on rentals), and applies the current task roles.
const RENAMED_TASKS = {
  'Due Diligence Deadline is Today: All Requests Due Today': 'Due Diligence Deadline is Today: Confirm Inspection Addendum is Executed and Returned',
  'First EMD Due Tomorrow': 'First EMD Due Tomorrow: Confirm has been sent or is scheduled to be sent',
  'Text Client: First EMD Due Tomorrow': 'Text Client: First EMD Due Tomorrow: Confirm it has been sent or is scheduled to be sent',
  'Second EMD Due: Confirm second earnest money has been sent or is scheduled to be sent': 'Second EMD Due Tomorrow: Confirm second earnest money has been sent or is scheduled to be sent',
  'Text Client: Schedule Final Walk Through': 'Text Client: Confirm Final Walk Through Date',
  'Text Other Agent: Schedule Final Walk Through': 'Text Other Agent: Let Them Know Final Walk Through Date',
};

export const cleanDealTasks = async (txId, deal) => {
  if (!txId || !deal) return;
  const snap = await get(ref(database, `transactions/${txId}/tasks`));
  if (!snap.exists()) return;
  const answers = [deal.homeSaleContingency, deal.underContract?.homeSaleContingency];
  const noHomeSale = !answers.includes('Yes') && answers.includes('No');
  const isRental = deal.type === 'Landlord' || deal.type === 'Tenant';
  const updates = {};
  const names = new Set(Object.values(snap.val() || {}).map(t => t?.name || ''));
  Object.entries(snap.val() || {}).forEach(([taskId, t]) => {
    const name = t.name || '';
    const path = `transactions/${txId}/tasks/${taskId}`;
    if (noHomeSale && /^Transaction has Home Sale Contingency/i.test(name)) { updates[path] = null; return; }
    if (!isRental && name === DUPLICATE_UTILITIES_TASK) { updates[path] = null; return; }
    const role = ROLE_CHANGES[name];
    if (role && t.role !== role) updates[`${path}/role`] = role;
    const newName = RENAMED_TASKS[name];
    if (newName) {
      if (names.has(newName)) { updates[path] = null; return; }
      updates[`${path}/name`] = newName;
      updates[`${path}/title`] = newName;
    }
  });
  if (Object.keys(updates).length) await update(ref(database), updates);
};

// One-time sweep: where a deal has both an old task name and its renamed version, the old copy is removed.
// Otherwise the old name is renamed in place.
const dedupeRenamedTasksRaw = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  let changed = 0;
  for (const txId of Object.keys(idx.val() || {})) {
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    const rows = Object.entries(snap.val() || {});
    const names = new Set(rows.map(([, t]) => t?.name || ''));
    const updates = {};
    rows.forEach(([taskId, t]) => {
      const name = t?.name || '';
      const newName = RENAMED_TASKS[name];
      if (!newName) return;
      const path = `transactions/${txId}/tasks/${taskId}`;
      if (names.has(newName)) { updates[path] = null; return; }
      updates[`${path}/name`] = newName;
      updates[`${path}/title`] = newName;
    });
    const n = Object.keys(updates).length;
    if (n) { await update(ref(database), updates); changed += n; }
  }
  return changed;
};

// One-time cleanup: the wire fraud reminder task is removed from the agent's side everywhere it was saved,
// including the saved admin task lists and existing deals. Copies assigned to TC stay.
const WIRE_FRAUD_TASK = 'Reminder to Client: Wire Fraud Before Closing';
export const removeWireFraudForAgent = async () => {
  let removed = 0;
  const listsSnap = await get(ref(database, 'taskLists'));
  if (listsSnap.exists()) {
    const updates = {};
    Object.entries(listsSnap.val() || {}).forEach(([listId, list]) => {
      Object.entries(list?.tasks || {}).forEach(([key, t]) => {
        if (t?.name === WIRE_FRAUD_TASK && t.role !== 'TC') updates[`taskLists/${listId}/tasks/${key}`] = null;
      });
    });
    const n = Object.keys(updates).length;
    if (n) { await update(ref(database), updates); removed += n; }
  }
  const idx = await get(ref(database, 'transactionIndex'));
  if (idx.exists()) {
    for (const txId of Object.keys(idx.val() || {})) {
      const snap = await get(ref(database, `transactions/${txId}/tasks`));
      if (!snap.exists()) continue;
      const updates = {};
      Object.entries(snap.val() || {}).forEach(([taskId, t]) => {
        if (t?.name === WIRE_FRAUD_TASK && t.role !== 'TC') updates[`transactions/${txId}/tasks/${taskId}`] = null;
      });
      const n = Object.keys(updates).length;
      if (n) { await update(ref(database), updates); removed += n; }
    }
  }
  return removed;
};

// Brings existing resale buyer deals in line with the task list in use: adds missing tasks, and updates
// role, sharing, and due dates on open tasks. Tasks removed from the list are left alone. Automatic emails
// are not added to deals that were created before them.
const syncResaleDealsToListRaw = async () => {
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  const defs = (await loadListTasks(BUYER_LIST_FOR_STATUS.resale)).filter(d => d.type !== 'Notification');
  let changed = 0;
  for (const [txId, entry] of Object.entries(idx.val() || {})) {
    const txSnap = await get(ref(database, `users/${entry.ownerUid}/transactions/${txId}`));
    if (!txSnap.exists()) continue;
    const tx = txSnap.val();
    if (tx.propertyStatus !== 'resale') continue;
    const taskSnap = await get(ref(database, `transactions/${txId}/tasks`));
    const byName = {};
    Object.entries(taskSnap.exists() ? taskSnap.val() : {}).forEach(([id, t]) => { byName[t.name] = { id, ...t }; });
    const updates = {};
    for (const def of defs) {
      if (def.requiresField && tx[def.requiresField] !== def.requiresValue) continue;
      if (def.requiresAnchor && !dateFromTransaction(tx, BUYER_ANCHOR_FIELDS[def.requiresAnchor])) continue;
      const field = BUYER_ANCHOR_FIELDS[def.anchor];
      const anchorDate = field ? dateFromTransaction(tx, field) : null;
      const dueDate = anchorDate ? addDays(anchorDate, def.offsetDays) : null;
      const have = byName[def.name];
      if (have) {
        const path = `transactions/${txId}/tasks/${have.id}`;
        if (have.role !== def.role) updates[`${path}/role`] = def.role;
        if (!!have.shared !== !!def.shared) updates[`${path}/shared`] = !!def.shared;
        const open = have.status !== 'completed' && have.status !== 'na';
        if (open && (have.dueDate || null) !== dueDate) updates[`${path}/dueDate`] = dueDate;
      } else {
        const key = push(ref(database, `transactions/${txId}/tasks`)).key;
        byName[def.name] = { id: key, status: 'pending', role: def.role, shared: def.shared === true };
        updates[`transactions/${txId}/tasks/${key}`] = {
          name: def.name,
          title: def.name,
          role: def.role,
          type: def.type || 'Task',
          appliesTo: def.appliesTo || 'All',
          mode: 'task',
          dueDate,
          status: 'pending',
          completedBy: null,
          completedAt: null,
          createdBy: entry.ownerUid,
          createdAt: new Date().toISOString(),
          anchor: def.anchor || null,
          offsetDays: Number(def.offsetDays) || 0,
          shared: def.shared === true,
          action: /did this transaction close today/i.test(def.name || '') ? 'enter-closing-date' : null,
        };
      }
    }
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      changed += n;
    }
  }
  return changed;
};

// One-time update to the task lists saved by the admin: applies renames, role changes and removals,
// and adds any starter task that is missing from a saved list. Landlord and tenant lists keep their utilities task.
const RETIRED_LIST_NAMES = new Set(['are there forms the buyer needs to complete for the hoa?', 'schedule final walk through', 'text cooperating agent: confirm final walk-through']);
const normName = (s) => String(s || '').trim().toLowerCase();
const updateSavedTaskListsRaw = async () => {
  const snap = await get(ref(database, 'taskLists'));
  if (!snap.exists()) return 0;
  const updates = {};
  Object.entries(snap.val() || {}).forEach(([listId, list]) => {
    const rows = list?.tasks || {};
    const keepUtilities = /landlord|tenant/i.test(listId);
    const haveNames = new Set();
    Object.entries(rows).forEach(([key, t]) => {
      const name = t?.name || '';
      const path = `taskLists/${listId}/tasks/${key}`;
      if (RETIRED_LIST_NAMES.has(normName(name))) { updates[path] = null; return; }
      if (!keepUtilities && normName(name) === normName(DUPLICATE_UTILITIES_TASK)) { updates[path] = null; return; }
      const newName = RENAMED_TASKS[name];
      if (newName) {
        updates[`${path}/name`] = newName;
        updates[`${path}/title`] = newName;
        haveNames.add(normName(newName));
      } else {
        haveNames.add(normName(name));
      }
      const role = ROLE_CHANGES[name];
      if (role && t.role !== role) updates[`${path}/role`] = role;
    });
    const starter = DEFAULT_TASK_LISTS[listId]?.tasks || [];
    starter.forEach((def) => {
      if (haveNames.has(normName(def.name))) return;
      const key = push(ref(database, `taskLists/${listId}/tasks`)).key;
      updates[`taskLists/${listId}/tasks/${key}`] = JSON.parse(JSON.stringify(def));
      haveNames.add(normName(def.name));
    });
  });
  const n = Object.keys(updates).length;
  if (n) await update(ref(database), updates);
  return n;
};

// Migrations can be started twice at once (for example, when the Tasks page mounts twice in development).
// Each one shares its running promise so the work is only done once per run.
const guarded = (fn) => {
  let running = null;
  return () => {
    if (!running) running = fn().finally(() => { running = null; });
    return running;
  };
};
export const syncResaleDealsToList = guarded(syncResaleDealsToListRaw);

// Buyer lists and the deal status each one applies to.
const BUYER_LIST_STATUS = { 'resale-buyer-under-contract': 'resale', 'new-construction-buyer-under-contract': 'new-construction' };

// When a task is added to a buyer list, adds it to every open deal of that type that does not already have it.
// Existing tasks on deals are never changed or removed, and closed deals are skipped. Automatic emails are not copied.
export const syncActiveDealsToList = async (listId) => {
  const status = BUYER_LIST_STATUS[listId];
  if (!status) return 0;
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  const defs = (await loadListTasks(listId)).filter(d => d.type !== 'Notification');
  let added = 0;
  for (const [txId, entry] of Object.entries(idx.val() || {})) {
    const txSnap = await get(ref(database, `users/${entry.ownerUid}/transactions/${txId}`));
    if (!txSnap.exists()) continue;
    const tx = txSnap.val();
    if (tx.propertyStatus !== status || tx.status === 'closed' || tx.actualClosingDate) continue;
    const taskSnap = await get(ref(database, `transactions/${txId}/tasks`));
    const names = new Set(Object.values(taskSnap.exists() ? taskSnap.val() : {}).map(t => t.name));
    const updates = {};
    for (const def of defs) {
      if (names.has(def.name)) continue;
      if (def.requiresField && tx[def.requiresField] !== def.requiresValue) continue;
      if (def.requiresAnchor && !dateFromTransaction(tx, BUYER_ANCHOR_FIELDS[def.requiresAnchor])) continue;
      const field = BUYER_ANCHOR_FIELDS[def.anchor];
      const anchorDate = field ? dateFromTransaction(tx, field) : null;
      const dueDate = anchorDate ? addDays(anchorDate, def.offsetDays) : null;
      const key = push(ref(database, `transactions/${txId}/tasks`)).key;
      updates[`transactions/${txId}/tasks/${key}`] = {
        name: def.name,
        title: def.name,
        role: def.role,
        type: def.type || 'Task',
        appliesTo: def.appliesTo || 'All',
        mode: 'task',
        dueDate,
        status: 'pending',
        completedBy: null,
        completedAt: null,
        createdBy: entry.ownerUid,
        createdAt: new Date().toISOString(),
        anchor: def.anchor || null,
        offsetDays: Number(def.offsetDays) || 0,
        shared: def.shared === true,
        action: /did this transaction close today/i.test(def.name || '') ? 'enter-closing-date' : null,
      };
      names.add(def.name);
    }
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      added += n;
    }
  }
  return added;
};
export const updateSavedTaskLists = guarded(updateSavedTaskListsRaw);
export const dedupeRenamedTasks = guarded(dedupeRenamedTasksRaw);

// One-time cleanup: removes exact duplicate task names on each deal and in the saved admin lists.
// Keeps the copy that already has a status (done, N/A) if there is one, otherwise the first copy.
const duplicateKeysToRemove = (rows) => {
  const groups = {};
  rows.forEach(([key, t]) => {
    const n = normName(t?.name);
    if (!n) return;
    if (!groups[n]) groups[n] = [];
    groups[n].push([key, t]);
  });
  const remove = [];
  Object.values(groups).forEach((group) => {
    if (group.length < 2) return;
    const keep = group.find(([, t]) => t.status && t.status !== 'pending') || group[0];
    group.forEach(([key]) => { if (key !== keep[0]) remove.push(key); });
  });
  return remove;
};
export const removeDuplicateTasks = guarded(async () => {
  let removed = 0;
  const updates = {};
  const lists = await get(ref(database, 'taskLists'));
  Object.entries(lists.exists() ? lists.val() : {}).forEach(([listId, list]) => {
    duplicateKeysToRemove(Object.entries(list?.tasks || {})).forEach((key) => {
      updates[`taskLists/${listId}/tasks/${key}`] = null;
    });
  });
  const idx = await get(ref(database, 'transactionIndex'));
  for (const txId of Object.keys(idx.exists() ? idx.val() : {})) {
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    duplicateKeysToRemove(Object.entries(snap.val() || {})).forEach((key) => {
      updates[`transactions/${txId}/tasks/${key}`] = null;
    });
  }
  removed = Object.keys(updates).length;
  if (removed) await update(ref(database), updates);
  return removed;
});

// Seller, landlord, and tenant lists: which deals each list applies to.
const openDeal = (tx) => {
  const status = String(tx?.status || '').toLowerCase();
  return status !== 'closed' && status !== 'lost' && !tx?.actualClosingDate;
};
const TEAM_LIST_TARGETS = {
  'seller-listing': { type: 'Seller', extra: () => true },
  'seller-under-contract-team': { type: 'Seller', extra: (tx) => !!tx.underContract },
  'landlord-rental-listing': { type: 'Landlord', extra: () => true },
  'tenant-rental': { type: 'Tenant', extra: () => true },
  'new-tenant-our-listing': { type: 'Tenant', extra: () => true },
  'new-tenant-not-our-listing': { type: 'Tenant', extra: () => true },
  // Post-closing tasks go to closed deals of any type.
  'post-closing-follow-up': { type: null, closed: true, extra: () => true },
};
const isClosedDeal = (tx) => tx?.status === 'closed' || !!tx?.actualClosingDate;

// When a task is added to a seller, landlord or tenant list, adds it to every open deal of that type
// that does not already have it. Existing tasks are never changed, and automatic emails are not copied.
export const syncTeamListToOpenDeals = async (listId) => {
  const target = TEAM_LIST_TARGETS[listId];
  if (!target) return 0;
  const idx = await get(ref(database, 'transactionIndex'));
  if (!idx.exists()) return 0;
  const defs = (await loadListTasks(listId)).filter(d => d.type !== 'Notification');
  const today = new Date().toISOString().slice(0, 10);
  let added = 0;
  for (const [txId, entry] of Object.entries(idx.val() || {})) {
    const txSnap = await get(ref(database, `users/${entry.ownerUid}/transactions/${txId}`));
    if (!txSnap.exists()) continue;
    const tx = txSnap.val();
    const typeOk = target.type ? tx.type === target.type : true;
    const stateOk = target.closed ? isClosedDeal(tx) : openDeal(tx);
    if (!typeOk || !stateOk || !target.extra(tx)) continue;
    const taskSnap = await get(ref(database, `transactions/${txId}/tasks`));
    const names = new Set(Object.values(taskSnap.exists() ? taskSnap.val() : {}).map(t => t.name));
    const updates = {};
    for (const def of defs) {
      if (names.has(def.name)) continue;
      if (!passesRequirement(def, tx)) continue;
      const anchorDate = def.anchor === 'created' ? today : (def.anchor ? teamDateFor(tx, def.anchor) : null);
      const dueDate = anchorDate ? addDays(anchorDate, def.offsetDays) : null;
      const key = push(ref(database, `transactions/${txId}/tasks`)).key;
      updates[`transactions/${txId}/tasks/${key}`] = {
        name: def.name,
        role: def.role,
        type: def.type || 'Task',
        appliesTo: def.appliesTo || 'All',
        mode: 'task',
        dueDate,
        status: 'pending',
        completedBy: null,
        completedAt: null,
        createdBy: entry.ownerUid,
        createdAt: new Date().toISOString(),
        anchor: def.anchor || null,
        offsetDays: Number(def.offsetDays) || 0,
        shared: def.shared === true,
        ...(def.description ? { description: def.description } : {}),
      };
      names.add(def.name);
    }
    const n = Object.keys(updates).length;
    if (n) {
      await update(ref(database), updates);
      added += n;
    }
  }
  return added;
};

// One entry point for the Task Lists screen: buyer lists and team lists each use their own rules.
export const syncListToOpenDeals = async (listId) => (
  BUYER_LIST_STATUS[listId] ? syncActiveDealsToList(listId) : syncTeamListToOpenDeals(listId)
);

// Task logic version. Bump this number whenever task dates or task lists change. On the next load, every account
// recalculates open task dates on all of its seller deals, including deals created before the change.
export const TASK_LOGIC_VERSION = 2;

export const refreshSellerTaskDates = async (uid) => {
  if (!uid) return { ran: false };
  const flagRef = ref(database, `users/${uid}/migrations/sellerTaskDates`);
  const flag = await get(flagRef);
  const done = flag.exists() ? Number(flag.val()?.version || 0) : 0;
  if (done >= TASK_LOGIC_VERSION) return { ran: false };
  const snap = await get(ref(database, `users/${uid}/transactions`));
  let updated = 0;
  if (snap.exists()) {
    for (const [id, tx] of Object.entries(snap.val())) {
      if (tx?.type !== 'Seller') continue;
      const res = await syncTeamTaskDates(id, { ...tx, id });
      updated += res.updated || 0;
    }
  }
  await set(flagRef, { version: TASK_LOGIC_VERSION, doneAt: new Date().toISOString(), tasksUpdated: updated });
  return { ran: true, updated };
};

// Admin tool: recalculates open task dates on every seller deal in the office, using the current task logic.
// Reads each deal through the transaction index, so it reaches deals created before the change.
export const refreshAllSellerTaskDates = async () => {
  const idxSnap = await get(ref(database, 'transactionIndex'));
  const result = { deals: 0, updated: 0, failed: 0 };
  if (!idxSnap.exists()) return result;
  for (const [id, entry] of Object.entries(idxSnap.val())) {
    const owner = entry?.ownerUid;
    if (!owner) continue;
    try {
      const txSnap = await get(ref(database, `users/${owner}/transactions/${id}`));
      if (!txSnap.exists()) continue;
      const tx = txSnap.val();
      if (tx?.type !== 'Seller') continue;
      result.deals += 1;
      const res = await syncTeamTaskDates(id, { ...tx, id });
      result.updated += res.updated || 0;
    } catch (err) {
      result.failed += 1;
      console.error('Could not refresh seller deal', id, err);
    }
  }
  return result;
};
